import { useCallback, useEffect, useRef, useState } from 'react';
import { ListPayload } from 'core/http/response';
import useSession from 'hooks/useSession';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { ListProductTemplateParams } from 'store/products-templates/client';
import { getProductTemplateClient } from 'store/products-templates/getProductTemplateClient';
import { ProductTemplate } from 'store/products-templates/products';

const emptyProductTemplates: ListPayload<ProductTemplate> = { list: [], pageNumber: 0, pageSize: 10, total: 0 };

interface Props {
    country?: DeclarationCountry;
    internalType?: DeclarationInternalType;
    messageType?: MessageTypes;
    autoFetch?: boolean;
}

const useProductTemplateList = ({ country, internalType, messageType, autoFetch = true }: Props) => {
    const { customerId } = useSession();
    const [productTemplates, setProductTemplates] = useState(emptyProductTemplates);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const latestRequestId = useRef(0);

    const isReady = Boolean(country && internalType && messageType);

    const refetch = useCallback(
        async (params?: Partial<ListProductTemplateParams>) => {
            const requestId = ++latestRequestId.current;
            if (!isReady) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);

            try {
                if (!country || !internalType || !messageType) {
                    throw new Error('Missing required params');
                }

                const client = getProductTemplateClient(country!, internalType!, messageType!);
                const response = await client.list(params);
                if (requestId !== latestRequestId.current) return;
                setProductTemplates(response);
            } catch (e) {
                if (requestId !== latestRequestId.current) return;
                setProductTemplates(emptyProductTemplates);
                setError(e);
            } finally {
                if (requestId === latestRequestId.current) setIsLoading(false);
            }
        },
        [isReady, country, internalType, messageType]
    );

    // A different tenant, country, type or form must never show the rows of the previous one,
    // so the list is cleared first. Paging and refetching after a save keep the rows.
    useEffect(() => {
        setProductTemplates(emptyProductTemplates);
        if (autoFetch) refetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, refetch]);

    return { productTemplates, isLoading, error, refetch };
};

export default useProductTemplateList;
